'use client'

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import { useParams } from 'next/navigation'

interface MarkdownPreviewProps {
  content: string
  className?: string
}

export function MarkdownPreview({ content, className = '' }: MarkdownPreviewProps) {
  const params = useParams()
  const projectId = params?.id as string

  // Process Redmine wiki syntax
  const processContent = (text: string): string => {
    if (!text) return ''

    // Convert [[Wiki Link]] to proper markdown links
    // [[Page Name]] -> [Page Name](/projects/{id}/wiki/Page_Name)
    // [[Page Name|Display Text]] -> [Display Text](/projects/{id}/wiki/Page_Name)
    let processed = text.replace(/\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g, (match, pageName, displayText) => {
      const linkText = displayText || pageName
      const pageSlug = encodeURIComponent(pageName.trim())
      const url = projectId
        ? `/projects/${projectId}/wiki/${pageSlug}`
        : `#${pageSlug}`
      return `[${linkText.trim()}](${url})`
    })

    // Convert {{macro}} syntax to code blocks for visibility
    processed = processed.replace(/\{\{([^}]+)\}\}/g, '`{{$1}}`')

    // Handle Redmine user mentions @username -> highlight
    processed = processed.replace(/@([a-zA-Z0-9_-]+)/g, '**@$1**')

    // Handle issue references #123 -> make them links
    processed = processed.replace(/#(\d+)/g, (match, issueId) => {
      const url = projectId
        ? `/projects/${projectId}/issues/${issueId}`
        : `/issues/${issueId}`
      return `[#${issueId}](${url})`
    })

    return processed
  }

  const processedContent = processContent(content)

  return (
    <div className={`prose prose-sm dark:prose-invert max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        components={{
          // Style links
          a: ({ node, ...props }) => (
            <a {...props} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer" />
          ),
          // Style code blocks
          code: ({ node, inline, ...props }: any) => {
            if (inline) {
              return <code {...props} className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono" />
            }
            return <code {...props} className="block bg-muted p-4 rounded-lg overflow-x-auto font-mono text-sm" />
          },
          // Style blockquotes
          blockquote: ({ node, ...props }) => (
            <blockquote {...props} className="border-l-4 border-primary/30 pl-4 italic text-muted-foreground" />
          ),
          // Style tables
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto">
              <table {...props} className="min-w-full divide-y divide-border" />
            </div>
          ),
          th: ({ node, ...props }) => (
            <th {...props} className="px-4 py-2 text-left text-sm font-semibold bg-muted/50" />
          ),
          td: ({ node, ...props }) => (
            <td {...props} className="px-4 py-2 text-sm border-t border-border" />
          ),
          // Style headings
          h1: ({ node, ...props }) => (
            <h1 {...props} className="text-3xl font-bold mt-8 mb-4 pb-2 border-b border-border" />
          ),
          h2: ({ node, ...props }) => (
            <h2 {...props} className="text-2xl font-semibold mt-6 mb-3" />
          ),
          h3: ({ node, ...props }) => (
            <h3 {...props} className="text-xl font-semibold mt-4 mb-2" />
          ),
          // Style lists
          ul: ({ node, ...props }) => (
            <ul {...props} className="list-disc list-inside space-y-1 my-4" />
          ),
          ol: ({ node, ...props }) => (
            <ol {...props} className="list-decimal list-inside space-y-1 my-4" />
          ),
          li: ({ node, ...props }) => (
            <li {...props} className="ml-4" />
          ),
          // Style paragraphs
          p: ({ node, ...props }) => (
            <p {...props} className="my-4 leading-7" />
          ),
          // Style horizontal rules
          hr: ({ node, ...props }) => (
            <hr {...props} className="my-8 border-border" />
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  )
}
